import React, { ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    debounce,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    TextField,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { GeozoneEdit } from "../Geozone-edit/Geozone-edit";
import { GeoZoneType, CircleZone, GeoZoneRecord, Zone } from "../model";
import CircleIcon from "@mui/icons-material/Circle";
import BuildIcon from "@mui/icons-material/Build";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { toJS } from "mobx";
import { useStore } from "../../../hooks/useStore";
import LoadingButton from "@mui/lab/LoadingButton";

const ZonesList = observer(({ filter }: { filter: string }) => {
    const { geozones } = useStore();
    const { zones } = geozones;

    const filteredZones = useMemo(() => {
        return toJS(zones).filter((z) => { 
            if(!z || !z.name) return;

            console.log(z.name);

            return z.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
        }
        );
    }, [zones, filter]);

    const handleZoneClick = useCallback(
        (zone: GeoZoneRecord) => {
            geozones.focusOnZone(zone.zone);
        },
        [zones]
    );

    const handleEditClick = useCallback(
        (zone: GeoZoneRecord) => {
            geozones.focusOnZone(zone.zone);
            geozones.startEditGeoZone(zone.id);
        },
        [zones]
    );

    const handleDeleteClick = useCallback(
        (zone: GeoZoneRecord) => {
            geozones.deleteGeoZone(zone.id);
        },
        [zones]
    );

    return (
        <Box sx={{ overflowY: "auto", flex: 1, marginBottom: "1rem" }}>
            <List sx={{ padding: 0 }}>
                {filteredZones.map((zone) => (
                    <ZoneListItem
                        key={zone.id}
                        zone={zone}
                        onDeleteClick={handleDeleteClick}
                        onEditClick={handleEditClick}
                        onZoneClick={handleZoneClick}
                    />
                ))}
            </List>
        </Box>
    );
});

interface ZoneListItemProps {
    zone: GeoZoneRecord;
    onZoneClick: (id: GeoZoneRecord) => void;
    onEditClick: (id: GeoZoneRecord) => void;
    onDeleteClick: (id: GeoZoneRecord) => void;
}
const ZoneListItem = memo(
    ({ zone, onZoneClick, onEditClick, onDeleteClick }: ZoneListItemProps) => {
        const { name } = zone;
        const handleZoneClick = () => {
            onZoneClick(zone);
        };

        const handleEditClick = (
            e: React.MouseEvent<HTMLButtonElement, MouseEvent>
        ) => {
            e.stopPropagation();
            e.preventDefault();
            onEditClick(zone);
        };

        const handleDeleteClick = (
            e: React.MouseEvent<HTMLButtonElement, MouseEvent>
        ) => {
            e.stopPropagation();
            e.preventDefault();
            onDeleteClick(zone);
        };

        return (
            <ListItemButton onClick={handleZoneClick}>
                <ListItemIcon>
                    <CircleIcon />
                </ListItemIcon>
                <ListItemText primary={name} />
                <IconButton size="small" onClick={handleEditClick}>
                    <BuildIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleDeleteClick}>
                    <DeleteForeverIcon fontSize="small" />
                </IconButton>
            </ListItemButton>
        );
    }
);

function GeozoneListSidebar() {
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");

    useEffect(() => {
        const debounceTimeout = setTimeout(() => {
            setDebouncedQuery(query);
        }, 150);
        return () => clearTimeout(debounceTimeout);
    }, [query])

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value)
    }
    const { geozones } = useStore();
    const { startNewGeoZone, isEditing } = geozones;

    return (
        <Box
            height="100%"
            minHeight="300px"
            display="flex"
            flexDirection="column"
            maxHeight={"calc(100% - 24px)"}
        >
            {isEditing || (
                <>
                    <TextField value={query} onChange={handleSearchChange} label="Поиск геозоны" />
                    <ZonesList filter={debouncedQuery} />
                    <LoadingButton loading={geozones.isDeleting || geozones.isPending} variant="contained" onClick={startNewGeoZone}>
                        Создать геозону
                    </LoadingButton>
                </>
            )}
            {isEditing && <GeozoneEdit />}
        </Box>
    );
}

export default observer(GeozoneListSidebar);
